import { useEffect, useRef, useState } from 'react';
import type { AskAnswer } from '@crmsy/shared';
import { askCrm, clearAskHistory, fetchAskHistory, ApiError } from '../lib/api.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { useAskEnabled } from '../lib/features.js';
import { toast } from '../lib/toast.js';
import { useT } from '../i18n/index.js';

export interface AskPanelProps {
  onClose: () => void;
  /** Open a cited contact: the Table searched for its name. */
  onOpenContact: (name: string) => void;
}

/**
 * Ask the CRM (§20, D-39): a side panel with the question history and an input. Answers
 * are read-only text; the contacts an answer cited are chips that jump to the Table.
 */
export function AskPanel({ onClose, onOpenContact }: AskPanelProps) {
  const t = useT();
  const enabled = useAskEnabled();
  const [history, setHistory] = useState<AskAnswer[] | null>(null);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAskHistory()
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [history, busy]);

  useKeys({ Escape: onClose }, { priority: PRIORITY.panel });

  const send = async () => {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    try {
      const a = await askCrm(q);
      setHistory((h) => [...(h ?? []), a]);
      setQuestion('');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('ask.failed'), { kind: 'error' });
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const clear = async () => {
    await clearAskHistory().catch(() => undefined);
    setHistory([]);
  };

  return (
    <>
      <div className="fixed inset-0 z-20 bg-ink/20" onMouseDown={onClose} />
      <aside aria-label={t('ask.title')} data-ask-panel className="fixed inset-y-0 right-0 z-20 flex w-full max-w-xl flex-col border-l border-line bg-paper shadow-2xl">
        <header className="flex items-center gap-2 border-b border-line px-4 py-3">
          <h2 className="flex-1 text-base font-semibold">✨ {t('ask.title')}</h2>
          {history && history.length > 0 && (
            <button type="button" className="btn" onClick={() => void clear()}>
              {t('ask.clear')}
            </button>
          )}
          <button type="button" className="btn" onClick={onClose} aria-label={t('panel.close')}>
            <kbd>Esc</kbd>
          </button>
        </header>
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm">
          {!enabled && <p className="rounded-lg bg-warn-soft px-3 py-2 text-warn-ink">{t('ask.off')}</p>}
          {history === null && <p className="text-muted">{t('ask.loading')}</p>}
          {history && history.length === 0 && enabled && (
            <div className="text-muted">
              <p>{t('ask.intro')}</p>
              <ul className="mt-2 list-disc pl-5">
                <li>{t('ask.example1')}</li>
                <li>{t('ask.example2')}</li>
                <li>{t('ask.example3')}</li>
              </ul>
            </div>
          )}
          {history?.map((a) => (
            <article key={a.id} className="mb-4" data-ask-entry>
              <div className="mb-1 flex items-baseline gap-2">
                <p className="flex-1 font-medium">{a.question}</p>
                <span className="text-[11px] text-muted">{a.at.slice(0, 16).replace('T', ' ')}</span>
              </div>
              <p className="whitespace-pre-wrap rounded-xl bg-surface px-3 py-2">{a.answer}</p>
              {a.contacts.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {a.contacts.map((c) => (
                    <button key={c.id} type="button" className="chip" onClick={() => onOpenContact(c.name)} data-ask-contact={c.id}>
                      {c.name} ↗
                    </button>
                  ))}
                </div>
              )}
            </article>
          ))}
          {busy && <p className="text-muted">{t('ask.thinking')}</p>}
        </div>
        <form
          className="border-t border-line p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            ref={inputRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            rows={2}
            placeholder={t('ask.placeholder')}
            disabled={!enabled}
            className="w-full resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />
          <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted">
            <span>{t('ask.hint')}</span>
            <button type="submit" className="btn btn-primary" disabled={!enabled || busy || !question.trim()}>
              {t('ask.send')}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
